import {
  GAP_POLICY_CONNECT,
  GAP_POLICY_GAP,
} from '../../../../core/types/gap-policy.js';
import {
  getNumericalThresholdValue,
  getThresholdValue,
} from '../../../../core/utils/apply-gap-policy.js';
import {
  getDatapointIsGap,
  type NumericalDatapoint,
  type TimeframeDatapoint,
} from '../../../../core/utils/get-data-point-is-gap.js';
import { isCategoricalAxis, isTimeAxis } from '../../../types/guards.js';
import type { XYChartAxisInternal } from '../../../types/xy-chart-internals.js';
import type { AreaDatapoint, LineDatapoint } from '../../../types/xy-chart.js';

/**
 * Gets the gap policy for a given axis.
 * - Categorical axes always return 'connect'
 * - Time axes return the configured gapPolicy or default to 'gap'
 * - Numerical/Log axes return the configured gapPolicy or default to 'connect'
 *
 * @param axis - The axis configuration
 * @returns The gap policy for the axis
 */
export function getAxisGapPolicyThreshold(axis: XYChartAxisInternal) {
  if (isCategoricalAxis(axis)) {
    return Infinity;
  }

  if (isTimeAxis(axis)) {
    return axis.gapPolicy === GAP_POLICY_CONNECT
      ? Infinity
      : getThresholdValue(axis.gapPolicy ?? GAP_POLICY_GAP);
  }

  return axis.gapPolicy === GAP_POLICY_CONNECT || axis.gapPolicy === undefined
    ? Infinity
    : getNumericalThresholdValue(axis.gapPolicy);
}

function getDatapointDimensionValues(
  datapoint: LineDatapoint | AreaDatapoint,
  axis: XYChartAxisInternal,
  accessorKeys: ['x0', 'x1'] | ['y0', 'y1'],
): TimeframeDatapoint | NumericalDatapoint {
  if (isTimeAxis(axis)) {
    return {
      start: datapoint[accessorKeys[0]] as Date,
      end: datapoint[accessorKeys[1]] as Date,
    };
  }

  return {
    start: (datapoint[accessorKeys[0]] ?? datapoint[accessorKeys[1]]) as number,
  };
}

export function getXDatapointDimensionValues(
  datapoint: LineDatapoint | AreaDatapoint,
  axis: XYChartAxisInternal,
): TimeframeDatapoint | NumericalDatapoint {
  return getDatapointDimensionValues(datapoint, axis, ['x0', 'x1']);
}

export function getYDatapointDimensionValues(
  datapoint: LineDatapoint | AreaDatapoint,
  axis: XYChartAxisInternal,
): TimeframeDatapoint | NumericalDatapoint {
  return getDatapointDimensionValues(datapoint, axis, ['y0', 'y1']);
}

/**
 * Detects if there's a gap between two consecutive datapoints based on axis gap policies.
 * Checks both X and Y axes if their gap policy thresholds are defined.
 *
 * @param lastDatapoint - The previous datapoint in the series
 * @param currentDatapoint - The current datapoint being processed
 * @param xAxis - X-axis configuration with gap policy
 * @param yAxis - Y-axis configuration with gap policy
 * @returns true if a gap is detected on either axis, false otherwise
 */

export function detectGapBetweenDatapoints(
  lastDatapoint: LineDatapoint | AreaDatapoint,
  currentDatapoint: LineDatapoint | AreaDatapoint,
  xAxis: XYChartAxisInternal,
  yAxis: XYChartAxisInternal,
): boolean {
  const xGapPolicyThreshold = getAxisGapPolicyThreshold(xAxis);
  const yGapPolicyThreshold = getAxisGapPolicyThreshold(yAxis);

  let isGap = false;
  // Check X-axis gap
  if (xGapPolicyThreshold !== Infinity) {
    const lastDatapointX = getXDatapointDimensionValues(lastDatapoint, xAxis);
    const currentDatapointX = getXDatapointDimensionValues(
      currentDatapoint,
      xAxis,
    );

    isGap = getDatapointIsGap(
      lastDatapointX,
      currentDatapointX,
      xGapPolicyThreshold,
    );
  }

  // Check Y-axis gap (if not already detected on X-axis)
  if (!isGap && yGapPolicyThreshold !== Infinity) {
    const lastDatapointY = getYDatapointDimensionValues(lastDatapoint, yAxis);
    const currentDatapointY = getYDatapointDimensionValues(
      currentDatapoint,
      yAxis,
    );

    isGap = getDatapointIsGap(
      lastDatapointY,
      currentDatapointY,
      yGapPolicyThreshold,
    );
  }

  return isGap;
}
